import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { StickiesService } from "../../generated/cadendar-rpc/cadendar/stickies/v1/stickies_pb";
import {
  cadendarRpcTransport,
  shouldRetryCadendarRpcRequest,
} from "../main/components/cadendarRpc";
import { useOwnerState } from "../users/hooks/useOwner";
export { getStickiesErrorMessage } from "./stickiesRpc";

export type StickyPositionMode = "pixel" | "relative";

export interface StickyPositionInput {
  offsetX: number;
  offsetY: number;
  anchorId: string;
  positionMode: StickyPositionMode;
  relativeX: number | null;
  relativeY: number | null;
}

export interface CreateStickyInput extends StickyPositionInput {
  content: string;
  year: number;
  week: number;
}

export interface UpdateStickyInput extends StickyPositionInput {
  id: number;
  content: string;
}

const stickiesClient = createClient(StickiesService, cadendarRpcTransport);

function stickiesQueryKey(ownerId: string | null, year: number, week: number) {
  return ["cadendar-rpc", "stickies", ownerId, year, week];
}

function validatePosition(input: StickyPositionInput) {
  if (
    !Number.isInteger(input.offsetX) ||
    !Number.isInteger(input.offsetY) ||
    input.anchorId.length === 0 ||
    input.anchorId.length > 100 ||
    (input.positionMode === "relative" &&
      (input.relativeX === null || input.relativeY === null)) ||
    (input.relativeX !== null && !Number.isFinite(input.relativeX)) ||
    (input.relativeY !== null && !Number.isFinite(input.relativeY))
  ) {
    throw new Error("La position de la note est invalide.");
  }
}

function validateCreateSticky(input: CreateStickyInput) {
  if (
    input.content.length === 0 ||
    input.content.length > 500 ||
    !Number.isInteger(input.year) ||
    input.year < 2020 ||
    input.year > 2100 ||
    !Number.isInteger(input.week) ||
    input.week < 1 ||
    input.week > 53
  ) {
    throw new Error("La note est invalide.");
  }
  validatePosition(input);
}

function validateUpdateSticky(input: UpdateStickyInput) {
  if (
    !Number.isInteger(input.id) ||
    input.id < 1 ||
    input.content.length === 0 ||
    input.content.length > 500
  ) {
    throw new Error("La note est invalide.");
  }
  validatePosition(input);
}

function positionToRpc(input: StickyPositionInput) {
  return {
    offsetX: input.offsetX,
    offsetY: input.offsetY,
    anchorId: input.anchorId,
    positionMode: input.positionMode,
    relativeX: input.relativeX ?? undefined,
    relativeY: input.relativeY ?? undefined,
  };
}

function positionModeFromRpc(positionMode: string): StickyPositionMode {
  if (positionMode === "pixel" || positionMode === "relative") {
    return positionMode;
  }
  throw new Error("La réponse contient une position de note invalide.");
}

function getNoOwnerError(ownerId: string | null, isOwnerLoading: boolean) {
  return !isOwnerLoading && !ownerId
    ? new Error("Praticien sélectionné introuvable.")
    : null;
}

export function useStickies({ year, week }: { year: number; week: number }) {
  const ownerState = useOwnerState();
  const ownerId = ownerState.owner;
  const queryClient = useQueryClient();
  const [mutationError, setMutationError] = useState<unknown>(null);

  useEffect(() => {
    setMutationError(null);
  }, [ownerId, year, week]);

  const invalidateStickies = async (mutationOwnerId: string) => {
    await queryClient.invalidateQueries({
      queryKey: ["cadendar-rpc", "stickies", mutationOwnerId],
    });
  };
  const stickiesQuery = useQuery({
    queryKey: stickiesQueryKey(ownerId, year, week),
    enabled: ownerId !== null,
    retry: shouldRetryCadendarRpcRequest,
    queryFn: async () => {
      if (!ownerId) {
        throw new Error("Praticien sélectionné introuvable.");
      }
      const response = await stickiesClient.getStickies({
        ownerId,
        year,
        week,
      });
      return response.stickies.map((sticky) => ({
        id: sticky.id,
        content: sticky.content,
        offsetX: sticky.offsetX,
        offsetY: sticky.offsetY,
        anchorId: sticky.anchorId,
        positionMode: positionModeFromRpc(sticky.positionMode),
        relativeX: sticky.relativeX ?? null,
        relativeY: sticky.relativeY ?? null,
      }));
    },
  });

  const createMutation = useMutation({
    mutationFn: async ({
      ownerId: mutationOwnerId,
      input,
    }: {
      ownerId: string;
      input: CreateStickyInput;
    }) => {
      validateCreateSticky(input);
      return stickiesClient.createSticky({
        ownerId: mutationOwnerId,
        sticky: {
          content: input.content,
          year: input.year,
          week: input.week,
          ...positionToRpc(input),
        },
      });
    },
    onSuccess: async (_, { ownerId: mutationOwnerId }) => {
      setMutationError(null);
      await invalidateStickies(mutationOwnerId);
    },
    onMutate: () => {
      setMutationError(null);
    },
    onError: (error) => {
      setMutationError(error);
    },
  });
  const updateMutation = useMutation({
    mutationFn: async ({
      ownerId: mutationOwnerId,
      input,
    }: {
      ownerId: string;
      input: UpdateStickyInput;
    }) => {
      validateUpdateSticky(input);
      return stickiesClient.updateSticky({
        ownerId: mutationOwnerId,
        id: input.id,
        sticky: {
          content: input.content,
          ...positionToRpc(input),
        },
      });
    },
    onSuccess: async (_, { ownerId: mutationOwnerId }) => {
      setMutationError(null);
      await invalidateStickies(mutationOwnerId);
    },
    onMutate: () => {
      setMutationError(null);
    },
    onError: (error) => {
      setMutationError(error);
    },
  });
  const archiveMutation = useMutation({
    mutationFn: async ({
      ownerId: mutationOwnerId,
      id,
    }: {
      ownerId: string;
      id: number;
    }) => {
      if (!Number.isInteger(id) || id < 1) {
        throw new Error("La note est invalide.");
      }
      return stickiesClient.archiveSticky({ ownerId: mutationOwnerId, id });
    },
    onSuccess: async (_, { ownerId: mutationOwnerId }) => {
      setMutationError(null);
      await invalidateStickies(mutationOwnerId);
    },
    onMutate: () => {
      setMutationError(null);
    },
    onError: (error) => {
      setMutationError(error);
    },
  });

  const ensureOwner = () => {
    if (!ownerId) {
      throw new Error("Praticien sélectionné introuvable.");
    }
    return ownerId;
  };

  return {
    stickies: stickiesQuery.data ?? [],
    isLoading:
      ownerState.isLoading || (ownerId !== null && stickiesQuery.isPending),
    isReady: ownerId !== null && stickiesQuery.isSuccess,
    error:
      ownerState.error ??
      getNoOwnerError(ownerId, ownerState.isLoading) ??
      stickiesQuery.error,
    mutationError,
    isMutating:
      createMutation.isPending ||
      updateMutation.isPending ||
      archiveMutation.isPending,
    createSticky: (input: CreateStickyInput) =>
      createMutation.mutateAsync({ ownerId: ensureOwner(), input }),
    updateSticky: (input: UpdateStickyInput) =>
      updateMutation.mutateAsync({ ownerId: ensureOwner(), input }),
    archiveSticky: (id: number) =>
      archiveMutation.mutateAsync({ ownerId: ensureOwner(), id }),
  };
}
